import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSnackbar } from 'notistack';
import {
  AmendableSection,
  StagedSections,
} from '../../@types/clinicalRecordAmendment';
import {
  ClinicalRecordAmendmentsService,
  apiErrorMessage,
} from '../../api/clinicalRecordAmendments.service';
import AmendRecordConfirmDialog from './AmendRecordConfirmDialog';
import AmendmentSignDialog from './AmendmentSignDialog';
import ClinicalRecordHistoryDialog from './ClinicalRecordHistoryDialog';
import { sectionLabel } from './amendmentLabels';

// ----------------------------------------------------------------------
//
// Clinical record lock & amendment state for the record currently open.
//
// Draft  → forms save normally.
// Signed → every clinical field is read-only (see ClinicalLockArea).
// Amending (a user with amend_signed_clinical_records confirmed "Amend Record")
//        → forms are editable again, but their Save only *stages* the section
//          payload here. Nothing reaches the database until the user gives a
//          reason and re-signs; then all staged sections are committed in one
//          request (new version + audit rows, server-side transaction).
//          Cancelling or leaving discards the staged changes.

export interface ClinicalRecordLockValue {
  clinicalRecordId: number | null;
  isSigned: boolean;
  isAmending: boolean;
  /** Signed and not being amended: clinical fields are read-only. */
  isLocked: boolean;
  canAmend: boolean;
  currentVersion: number;
  isAmended: boolean;
  staged: StagedSections;
  stagedSections: AmendableSection[];
  /**
   * Call at the top of a form's save handler with the payload it would send.
   * Returns true when the record is being amended and the payload was staged:
   * the caller must then skip its API call.
   */
  stageIfAmending: (
    section: AmendableSection,
    payload: Record<string, unknown>,
  ) => boolean;
  requestAmend: () => void;
  cancelAmend: () => void;
  openReview: () => void;
  openHistory: () => void;
  /** True if there are staged changes and the user agreed to discard them (or there were none). */
  confirmDiscard: () => boolean;
}

const noop = () => undefined;

const UNLOCKED: ClinicalRecordLockValue = {
  clinicalRecordId: null,
  isSigned: false,
  isAmending: false,
  isLocked: false,
  canAmend: false,
  currentVersion: 0,
  isAmended: false,
  staged: {},
  stagedSections: [],
  stageIfAmending: () => false,
  requestAmend: noop,
  cancelAmend: noop,
  openReview: noop,
  openHistory: noop,
  confirmDiscard: () => true,
};

const ClinicalRecordLockContext =
  createContext<ClinicalRecordLockValue>(UNLOCKED);

/** Outside a provider (e.g. forms used elsewhere) everything behaves as an unlocked draft. */
export function useClinicalRecordLock(): ClinicalRecordLockValue {
  return useContext(ClinicalRecordLockContext);
}

export interface ClinicalRecordForLock {
  id: number;
  signedAt?: string | null;
  currentVersion?: number;
}

type Props = {
  record?: ClinicalRecordForLock | null;
  /** Reload the record list and the open record's section data after a commit. */
  onCommitted: () => Promise<unknown> | void;
  children: ReactNode;
};

const service = new ClinicalRecordAmendmentsService();

export function ClinicalRecordLockProvider({
  record,
  onCommitted,
  children,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [canAmend, setCanAmend] = useState(false);
  const [reasonPresets, setReasonPresets] = useState<string[]>([]);
  const [amending, setAmending] = useState<{
    recordId: number;
    version: number;
  } | null>(null);
  const [staged, setStaged] = useState<StagedSections>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const recordId = record?.id ?? null;
  const isSigned = !!record?.signedAt;
  const currentVersion = isSigned
    ? Math.max(record?.currentVersion ?? 0, 1)
    : 0;
  const isAmending = !!amending && amending.recordId === recordId;
  const stagedSections = useMemo(
    () =>
      (Object.keys(staged) as AmendableSection[]).filter(
        (k) => staged[k] !== undefined,
      ),
    [staged],
  );

  useEffect(() => {
    service
      .getOptions()
      .then(({ data }) => {
        setCanAmend(!!data.canAmend);
        setReasonPresets(data.reasonPresets ?? []);
      })
      .catch(() => setCanAmend(false));
  }, []);

  // Switching record (or the record becoming unsigned/refetched) ends an amendment.
  useEffect(() => {
    if (amending && amending.recordId !== recordId) {
      setAmending(null);
      setStaged({});
    }
  }, [amending, recordId]);

  // Staged changes only live in this page: warn before a reload/close loses them.
  useEffect(() => {
    if (!isAmending || !stagedSections.length) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isAmending, stagedSections.length]);

  const stageIfAmending = useCallback(
    (section: AmendableSection, payload: Record<string, unknown>) => {
      if (!isAmending) return false;
      setStaged((prev) =>
        section === 'trails'
          ? { ...prev, trails: [...(prev.trails ?? []), payload] }
          : { ...prev, [section]: payload },
      );
      enqueueSnackbar(
        `${sectionLabel(section)} changes added to the amendment. They are not saved until you sign the amendment.`,
        { variant: 'info' },
      );
      return true;
    },
    [isAmending, enqueueSnackbar],
  );

  const confirmDiscard = useCallback(() => {
    if (!isAmending || !stagedSections.length) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(
      'You have unsigned amendment changes. Leaving will discard them and nothing will be saved. Continue?',
    );
  }, [isAmending, stagedSections.length]);

  const cancelAmend = useCallback(() => {
    if (!confirmDiscard()) return;
    setAmending(null);
    setStaged({});
    enqueueSnackbar('Amendment cancelled. The signed record is unchanged.', {
      variant: 'info',
    });
  }, [confirmDiscard, enqueueSnackbar]);

  const confirmAmend = useCallback(async () => {
    if (!recordId) return;
    setOpening(true);
    try {
      const { data } = await service.open(recordId);
      setAmending({ recordId, version: data.currentVersion });
      setStaged({});
      setConfirmOpen(false);
    } catch (error) {
      enqueueSnackbar(apiErrorMessage(error, 'Could not start the amendment'), {
        variant: 'error',
      });
    } finally {
      setOpening(false);
    }
  }, [recordId, enqueueSnackbar]);

  const commit = useCallback(
    async (input: {
      reason: string;
      reasonPreset?: string;
      signatureUrl: string;
    }) => {
      if (!recordId || !amending) return;
      setCommitting(true);
      try {
        const { data } = await service.commit(recordId, {
          expectedVersion: amending.version,
          reason: input.reason,
          reasonPreset: input.reasonPreset,
          signatureUrl: input.signatureUrl,
          sections: staged,
        });
        setReviewOpen(false);
        setAmending(null);
        setStaged({});
        setHistoryVersion((v) => v + 1);
        enqueueSnackbar(
          `Amendment signed and saved as version ${data.versionNumber} (${data.changeCount} field${
            data.changeCount === 1 ? '' : 's'
          } changed).`,
          { variant: 'success' },
        );
        await onCommitted();
      } catch (error) {
        enqueueSnackbar(
          apiErrorMessage(
            error,
            'The amendment could not be saved. Nothing was changed.',
          ),
          {
            variant: 'error',
          },
        );
      } finally {
        setCommitting(false);
      }
    },
    [recordId, amending, staged, enqueueSnackbar, onCommitted],
  );

  const value = useMemo<ClinicalRecordLockValue>(
    () => ({
      clinicalRecordId: recordId,
      isSigned,
      isAmending,
      isLocked: isSigned && !isAmending,
      canAmend,
      currentVersion,
      isAmended: currentVersion > 1,
      staged,
      stagedSections,
      stageIfAmending,
      requestAmend: () => setConfirmOpen(true),
      cancelAmend,
      openReview: () => setReviewOpen(true),
      openHistory: () => setHistoryOpen(true),
      confirmDiscard,
    }),
    [
      recordId,
      isSigned,
      isAmending,
      canAmend,
      currentVersion,
      staged,
      stagedSections,
      stageIfAmending,
      cancelAmend,
      confirmDiscard,
    ],
  );

  return (
    <ClinicalRecordLockContext.Provider value={value}>
      {children}

      <AmendRecordConfirmDialog
        open={confirmOpen}
        loading={opening}
        version={currentVersion}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmAmend}
      />

      <AmendmentSignDialog
        open={reviewOpen}
        loading={committing}
        stagedSections={stagedSections}
        staged={staged}
        reasonPresets={reasonPresets}
        nextVersion={(amending?.version ?? currentVersion) + 1}
        onClose={() => setReviewOpen(false)}
        onSubmit={commit}
      />

      {recordId && (
        <ClinicalRecordHistoryDialog
          open={historyOpen}
          clinicalRecordId={recordId}
          refreshKey={historyVersion}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </ClinicalRecordLockContext.Provider>
  );
}

/**
 * Exposes the current lock value to a parent that renders the provider (e.g. to
 * guard record switching in event handlers). Render inside the provider.
 */
export function ClinicalRecordLockBridge({
  lockRef,
}: {
  lockRef: { current: ClinicalRecordLockValue | null };
}) {
  // eslint-disable-next-line no-param-reassign
  lockRef.current = useClinicalRecordLock();
  return null;
}
